import { useCallback } from 'react';

import { FontAwesomeIcon, Input } from '@appcharge/shared-ui';
import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { EChecklistTaskStatus, ETaskId, PublisherInfo } from 'common/contracts';

import { ESavingForm } from '../DeveloperSettings.types';
import { getNavigationPath } from '../helpers/developerSettings.helpers';
import { useProjectInfoForm } from '../hooks/useProjectInfoForm';

import { SettingsCard } from './SettingsCard';

interface IProjectInfoSectionProps {
  publisherData: PublisherInfo | undefined;
  updatePublisher: UseMutationResult<unknown, unknown, Partial<PublisherInfo>>;
  getPublisher: UseQueryResult<PublisherInfo, Error>;
  savingForm: ESavingForm;
  setSavingForm: (form: ESavingForm) => void;
  isSuperAdmin: boolean;
  hasCategories: boolean;
  isWebStore: boolean;
  isWebCheckout: boolean;
  setTaskStatus: (taskId: ETaskId, status?: EChecklistTaskStatus) => void;
  onCopy: (value: string, label: string) => void;
  title?: string;
}

const DEFAULT_TITLE = 'Project Info';

export const ProjectInfoSection: React.FC<IProjectInfoSectionProps> = ({
  publisherData,
  updatePublisher,
  getPublisher,
  savingForm,
  setSavingForm,
  isSuperAdmin,
  hasCategories,
  isWebStore,
  isWebCheckout,
  setTaskStatus,
  onCopy,
  title = DEFAULT_TITLE
}) => {
  const form = useProjectInfoForm({
    publisherData,
    updatePublisher,
    getPublisher,
    hasCategories,
    isWebStore,
    isWebCheckout,
    setTaskStatus,
    setSavingForm
  });

  const handleOpenDomain = useCallback((): void => {
    if (!form.values.domain) {
      return;
    }
    const navigationPath = getNavigationPath(form.values.domain);
    window.open(navigationPath, '_blank');
  }, [form.values.domain]);

  const handleCopyProjectId = useCallback((): void => {
    onCopy(publisherData?._id || '', 'Project ID');
  }, [onCopy, publisherData?._id]);

  return (
    <SettingsCard
      title={title}
      description="Your store project details."
      onSave={form.submitForm}
      isSaveDisabled={
        !form.isValid ||
        !form.dirty ||
        savingForm !== ESavingForm.NONE
      }
      isLoading={savingForm === ESavingForm.PROJECT_INFO}
      saveButtonDataTestId="project-info-save"
    >
      <div className="grid grid-cols-3 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Store project name
          </label>
          <Input
            name="companyName"
            value={form.values.companyName}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            placeholder="Store project name"
            dataTestId="project-info-company-name"
            state={
              form.touched.companyName &&
                form.errors.companyName
                ? 'error'
                : 'default'
            }
            description={
              form.touched.companyName
                ? (form.errors.companyName as string)
                : undefined
            }
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Project ID
          </label>
          <Input
            value={publisherData?._id || ''}
            disabled
            dataTestId="project-info-project-id"
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-copy"
                onClick={handleCopyProjectId}
              />
            }
            rightIconStyles="cursor-pointer text-base-mutedForeground"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Domain</label>
          <Input
            name="domain"
            value={form.values.domain}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            disabled={!isSuperAdmin}
            dataTestId="project-info-domain"
            state={
              form.touched.domain && form.errors.domain
                ? 'error'
                : 'default'
            }
            description={
              form.touched.domain
                ? (form.errors.domain as string)
                : undefined
            }
            rightIcon={
              <FontAwesomeIcon
                icon="fa-regular fa-arrow-up-right-from-square"
                onClick={handleOpenDomain}
              />
            }
            rightIconStyles="cursor-pointer text-base-mutedForeground"
          />
        </div>
      </div>
    </SettingsCard>
  );
};
